import "./Buckets.css";

import * as m from "../../../model/model";

import {
  deleteFileThunk,
  listFilesThunk,
  renameFileThunk,
  uploadFileThunk,
} from "../../../store/slices/adminThunks";

import ConfirmModal from "../../../modals/ConfirmModal";
import PromptModal from "../../../modals/PromptModal";
import React from "react";
import { useAppDispatch } from "../../../store";
import { useLocale } from "../../../hooks/useLocale";
import { useModal } from "../../../context/ModalContext";

export type BucketPermission =
  | "list"
  | "upload"
  | "download"
  | "delete"
  | "rename"
  | "new-folder"
  | "admin";

const icon = (file: m.File) => {
  const fa = () => {
    if (file.isDirectory) {
      return "fa-folder-o";
    }
    if (file.contentType.startsWith("image/")) {
      return "fa-file-image-o";
    }
    if (file.contentType.startsWith("audio/")) {
      return "fa-file-audio-o";
    }
    if (file.contentType.startsWith("video/")) {
      return "fa-file-video-o";
    }
    if (file.contentType.startsWith("application/pdf")) {
      return "fa-file-pdf-o";
    }
    if (file.contentType.endsWith("officedocument.wordprocessingml.document")) {
      return "fa-file-word-o";
    }
    if (
      file.contentType.endsWith("officedocument.presentationml.presentation")
    ) {
      return "fa-file-powerpoint-o";
    }
    if (file.contentType.endsWith("officedocument.spreadsheetml.sheet")) {
      return "fa-file-excel-o";
    }
    return "fa-file-o";
  };

  return <i className={`fa ${fa()}`} aria-hidden="true" />;
};

interface BucketProps {
  root: string;
  permissions: BucketPermission[];
}

export default function Buckets(props: BucketProps) {
  const { root, permissions } = props;
  const [files, setFiles] = React.useState<m.FilesResponse>();
  const [currentPath, setCurrentPath] = React.useState<string>();
  const [selectedFile, setSelectedFile] = React.useState<m.File | null>(null);

  const dispatch = useAppDispatch();
  const { showModal } = useModal();
  const { collator } = useLocale();

  React.useEffect(() => {
    if (currentPath !== undefined && hasPermission("list")) {
      const prefix = currentPath.endsWith("/")
        ? currentPath
        : currentPath + (currentPath.length ? "/" : "");

      dispatch(listFilesThunk({ prefix }))
        .unwrap()
        .then((resp) => {
          setFiles(resp);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  React.useEffect(() => {
    setCurrentPath(root);
    setSelectedFile(null);
  }, [root]);

  const hasPermission = (perm: BucketPermission) => {
    return permissions.includes("admin") || permissions.includes(perm);
  };

  const onClickFile = (file: m.File) => {
    setSelectedFile(file);
  };

  const onClickPath = (path: string) => {
    console.log("click path", path);
    setCurrentPath(path);
    setSelectedFile(null);
  };

  const onClickUpload = (evt: React.MouseEvent) => {
    evt.preventDefault();
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = async () => {
      if (!input.files || input.files.length === 0) {
        return;
      }
      const file = input.files[0];
      dispatch(uploadFileThunk({ file, path: currentPath ?? root }))
        .unwrap()
        .then((flies) => {
          setFiles(flies);
          const targetFile = flies?.files.find((f) =>
            f.name.endsWith(file.name)
          );
          setSelectedFile(targetFile || null);
        });
    };
    input.click();
  };

  const onClickNewFolder = (evt: React.MouseEvent) => {
    evt.preventDefault();
    showModal(
      <PromptModal
        header="New Folder"
        message="Enter folder name"
        onConfirm={(folderName) => {
          dispatch(
            uploadFileThunk({
              file: new File([], folderName),
              path: currentPath + "/" + folderName + "/",
            })
          )
            .unwrap()
            .then((resp) => {
              setFiles(resp);
              setCurrentPath(resp.prefix);
            });
        }}
        onCancel={() => null}
      />
    );
  };

  const onClickRename = (evt: React.MouseEvent) => {
    evt.preventDefault();
    if (!selectedFile) {
      return;
    }
    showModal(
      <PromptModal
        header={`Rename ${selectedFile.displayName}`}
        message="Enter new name"
        value={selectedFile.displayName}
        note="Remember to copy new path to Airtable"
        onConfirm={(name) => {
          const newName = currentPath + "/" + name.replace(/\//g, "-");
          dispatch(
            renameFileThunk({
              oldName: selectedFile.name,
              newName,
            })
          )
            .unwrap()
            .then((flies) => {
              setFiles(flies);
              const targetFile = flies?.files.find((f) => f.name === newName);
              setSelectedFile(targetFile || null);
            });
        }}
        onCancel={() => null}
      />
    );
  };

  const onClickDelete = () => {
    if (!selectedFile) {
      return;
    }
    showModal(
      <ConfirmModal
        message={`Delete ${selectedFile.name}?`}
        onConfirm={() => {
          dispatch(deleteFileThunk({ path: selectedFile.name }))
            .unwrap()
            .then((files) => {
              setFiles(files);
              setSelectedFile(null);
            });
        }}
        onCancel={() => null}
      />
    );
  };

  const onClickDownload = async () => {
    if (!selectedFile) {
      return;
    }
    const url = await fetchSignedUrl(selectedFile.name);
    window.open(url, "_blank");
  };

  const renderPath = () => {
    const parts = currentPath
      ?.replace(root, "")
      .split("/")
      .filter((p) => !!p);

    console.log({ root, currentPath, parts });

    const first = (
      <>
        <span className="path-part" onClick={() => onClickPath(root)}>
          root
        </span>
        <span> {parts?.length ? "/" : ""} </span>
      </>
    );

    const path = parts
      ?.filter((p) => !!p)
      .map((part, i) => (
        <React.Fragment key={i}>
          <span
            className={`path-part ${i === parts.length - 1 ? "current" : ""}`}
            onClick={() =>
              onClickPath(
                (root.length ? `${root}/` : "") +
                  parts.slice(0, i + 1).join("/")
              )
            }
          >
            {part}
          </span>

          <span>{i < parts.length - 1 ? "/" : ""}</span>
        </React.Fragment>
      ));

    return (
      <>
        {first} {path}
      </>
    );
  };

  const localUrl = (fullPath: string) => {
    return `/app/bucket/fetch/${encodeURIComponent(fullPath)}`;
  };

  const fetchSignedUrl = async (filename: string) => {
    const response = await fetch(`/app/bucket/signed-url?filename=${filename}`);
    const json = await response.json();
    return json.url;
  };

  const renderSelectedFile = () => {
    if (!selectedFile) return null;

    // const url = selectedFile?.tokenUrl;
    const url = localUrl(selectedFile.name);

    if (selectedFile.contentType.startsWith("image/")) {
      return (
        <div className="bucket-media-container">
          <img
            src={url}
            alt={selectedFile.displayName}
            className="bucket-image"
          />
        </div>
      );
    }

    if (selectedFile.contentType.startsWith("audio/")) {
      return (
        <div className="bucket-media-container">
          <audio controls className="bucket-audio">
            <source src={url} type={selectedFile.contentType} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    if (selectedFile.contentType.startsWith("video/")) {
      return (
        <div className="bucket-media-container">
          <video controls className="bucket-video">
            <source src={url} type={selectedFile.contentType} />
            Your browser does not support the video element.
          </video>
        </div>
      );
    }

    if (selectedFile.contentType.startsWith("application/pdf")) {
      return (
        <div className="bucket-media-container">
          <object
            data={url}
            type={selectedFile.contentType}
            width="100%"
            height="600"
            aria-label={selectedFile.displayName}
          >
            <embed src={url} type={selectedFile.contentType} />
          </object>
        </div>
      );
    }

    if (
      selectedFile.contentType.endsWith(
        "officedocument.presentationml.presentation"
      )
    ) {
      return (
        <div className="bucket-media-container">
          <iframe
            src={url}
            title={selectedFile.displayName}
            width="100%"
            height="600"
          />
        </div>
      );
    }

    return (
      <div>
        <a href={selectedFile.url} target="_blank" rel="noreferrer">
          Download {selectedFile.displayName}
        </a>
      </div>
    );
  };

  const renderViewHeader = () => {
    if (!selectedFile) {
      return <div>Select a file to view</div>;
    }

    return (
      <>
        <div className="bucket-view-item-info">
          <div>{selectedFile.displayName}</div>
          <div>{selectedFile.contentType}</div>
          <div>{selectedFile.size}</div>
          <div>{selectedFile.updated}</div>
        </div>
        <div className="bucket-view-item-actions">
          {hasPermission("upload") && (
            <button onClick={() => null}>(Replace)</button>
          )}
          {hasPermission("rename") && (
            <button onClick={onClickRename}>Rename</button>
          )}
          {hasPermission("delete") && (
            <button onClick={onClickDelete}>Delete</button>
          )}
          {hasPermission("download") && (
            <button onClick={onClickDownload}>Download</button>
          )}
          {hasPermission("admin") && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(localUrl(selectedFile.name));
              }}
            >
              Copy Path
            </button>
          )}
        </div>
      </>
    );
  };

  if (!files) {
    return <div>Loading...</div>;
  }

  return (
    <div className="loa-bucket">
      <div className="bucket-header">
        <div className="bucket-path">{renderPath()}</div>
        <div className="bucket-actions">
          {hasPermission("new-folder") && (
            <button className="loa-button" onClick={onClickNewFolder}>
              New Folder
            </button>
          )}
          {hasPermission("upload") && (
            <button className="loa-button" onClick={onClickUpload}>
              Upload
            </button>
          )}
        </div>
      </div>
      <div className="bucket-panel">
        <div className="bucket-files">
          {[...files.directories]
            .sort((d1, d2) => collator.compare(d1.displayName, d2.displayName))
            .map((dir) => (
              <div
                key={dir.name}
                aria-details={dir.name}
                className="bucket-item-row"
              >
                <div>{icon(dir)}</div>
                <div onClick={() => onClickPath(dir.name)}>
                  {dir.displayName}
                </div>
                <div />
              </div>
            ))}
          {[...files.files]
            .sort((f1, f2) => collator.compare(f1.displayName, f2.displayName))
            .map((file) => (
              <div
                key={file.name}
                aria-details={file.name}
                className="bucket-item-row"
              >
                <div>{icon(file)}</div>
                <div onClick={() => onClickFile(file)}>{file.displayName}</div>
                <div>{(file.size / 1024 / 1024).toFixed(1)}</div>
              </div>
            ))}
        </div>
        <div className="bucket-view">
          <div className="bucket-view-header">{renderViewHeader()}</div>
          <div className="bucket-view-content">{renderSelectedFile()}</div>
        </div>
      </div>
    </div>
  );
}
